"use strict";
/**
 * HOOPER — FORMULE CANONICHE (@leopart/calc).
 *
 * ⚠️ Qui vivono DUE formule DISTINTE e NON interscambiabili. Il censimento
 * 2026-07-26 (vault/Architettura/Calcoli e formule.md, danno attivo #4) le
 * aveva trovate sparse nel backend con altre varianti divergenti: queste due
 * sono le SOPRAVVISSUTE LEGITTIME, con scopi e consumer diversi. Non vanno
 * unificate "per pulizia": cambiarne una altera dati persistiti o il RI.
 *
 * 1) `hooperComposite` — il COMPOSITO DEL QUESTIONARIO Hooper.
 *    - Scala: opera sui 4 item GREZZI 1-5 e restituisce 1-5 (media pesata).
 *    - Item: sonno (sleepValue, eventualmente già composto con le ore via
 *      computeSleepValue), fatica, DOMS, stress. Variante a 5 item quando
 *      c'è readinessToTrain (RTT): i pesi base scalano ×0.85 e RTT pesa 0.15.
 *    - Pesi base: sonno .30 · fatica .25 · doms .25 · stress .20.
 *    - NESSUN riproporzionamento: il chiamante passa tutti gli item.
 *    - CHI LO USA: write-path questionari (hooper-questionnaires.service,
 *      readiness.service submitHooper) → campo PERSISTITO
 *      `hooperQuestionnaires.average`, da cui a valle dipende l'AFI
 *      (Antifragility Index). Toccare i pesi ⇒ ricalcolo massivo di
 *      `average` + AFI (pipeline P2).
 *
 * 2) `mentaleScore` — il FATTORE "MENTALE" DEL RECOVERY INDEX (spec sez. 3.3).
 *    - Scala: converte ogni item 1-5 in recovery % via `hooperItemRecovery`
 *      e restituisce 0-100 (arrotondato a 2 decimali).
 *    - Item DIVERSI dal composito: sonno, fatica, stress, UMORE (mood).
 *      Il DOMS qui NON c'è: alimenta il fattore Muscolare del RI, non il
 *      Mentale.
 *    - Pesi: sonno .30 · fatica .30 · stress .20 · umore .20 — CON
 *      riproporzionamento automatico sugli item mancanti (undefined).
 *    - CHI LO USA: recovery.service.calculateHooperRecovery → upsert
 *      `recoveryFactors` (type=HOOPER) → `totalRecoveries.mentaleScore`
 *      → RI (readiness, mobile, alerts).
 *
 * I wrapper backend delegano qui con firme invariate:
 *  - computeHooperScore  → apps/backend/src/utils/interpolationRecovery.ts
 *  - computeMentaleScore / computeHooperItemRecovery →
 *    apps/backend/src/services/recovery/recovery-calculations.ts
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.hooperComposite = hooperComposite;
exports.hooperItemRecovery = hooperItemRecovery;
exports.mentaleScore = mentaleScore;
/**
 * Media ponderata Hooper sui valori grezzi 1-5 → composito 1-5.
 * Senza RTT: .30/.25/.25/.20. Con RTT: pesi base ×0.85 + RTT 0.15
 * (= .255/.2125/.2125/.17/.15). Vedi header del file per chi la consuma.
 */
function hooperComposite(params) {
    const { sleepValue, fatigue, doms, stress, readinessToTrain } = params;
    if (readinessToTrain != null) {
        return (sleepValue * 0.255 +
            fatigue * 0.2125 +
            doms * 0.2125 +
            stress * 0.17 +
            readinessToTrain * 0.15);
    }
    return sleepValue * 0.3 + fatigue * 0.25 + doms * 0.25 + stress * 0.2;
}
// ─── 2) Fattore Mentale del RI (scala 0-100, spec sez. 3.2/3.3) ─────────────
/**
 * Score Hooper per singolo item (1-5) → recovery % (spec sez. 3.2).
 * Formula: (score - 1) / 4 × 100, con clamp dell'input a [1,5].
 */
function hooperItemRecovery(score) {
    const clamped = Math.max(1, Math.min(5, score));
    return ((clamped - 1) / 4) * 100;
}
/**
 * Fattore Mentale del RI (spec sez. 3.3): weighted mean dei 4 sub-item
 * convertiti in % con riproporzionamento sugli item mancanti.
 * Pesi: sonno 0.30, fatica 0.30, stress 0.20, umore 0.20.
 * `undefined` se nessun item disponibile.
 */
function mentaleScore(params) {
    const WEIGHTS = { sonno: 0.3, fatica: 0.3, stress: 0.2, umore: 0.2 };
    const items = [
        { key: 'sonno', value: params.sleepQuality },
        { key: 'fatica', value: params.fatigue },
        { key: 'stress', value: params.stressLevel },
        { key: 'umore', value: params.mood },
    ];
    const available = items.filter((i) => i.value !== undefined);
    if (available.length === 0)
        return undefined;
    const totalWeight = available.reduce((sum, i) => sum + WEIGHTS[i.key], 0);
    let score = 0;
    for (const item of available) {
        const pct = hooperItemRecovery(item.value);
        score += pct * (WEIGHTS[item.key] / totalWeight);
    }
    return Math.round(score * 100) / 100;
}
